import React from 'react';
import { FileText, User, DollarSign, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

interface ApprovalWorkflowProps {
  approverName: string | null;
  isLoading?: boolean;
}

const WorkflowStep = ({ 
  icon: Icon, 
  title, 
  subtitle, 
  isLast = false 
}: { 
  icon: React.ElementType; 
  title: string; 
  subtitle: string; 
  isLast?: boolean;
}) => (
  <div className="flex items-center">
    <div className="flex flex-col items-center text-center min-w-[100px] sm:min-w-[140px]">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
      </div>
      <span className="text-xs sm:text-sm font-medium text-foreground">{title}</span>
      <span className="text-xs text-muted-foreground mt-0.5 max-w-[120px] truncate">{subtitle}</span>
    </div>
    {!isLast && (
      <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground mx-2 sm:mx-4 flex-shrink-0" />
    )}
  </div>
);

export const ApprovalWorkflow: React.FC<ApprovalWorkflowProps> = ({ 
  approverName, 
  isLoading = false 
}) => {
  if (isLoading) {
    return (
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading approval workflow...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-muted/30">
      <CardContent className="py-4">
        <p className="text-xs text-muted-foreground mb-3 text-center sm:text-left">
          Approval Workflow
        </p>
        <div className="flex items-center justify-center overflow-x-auto pb-2">
          <WorkflowStep
            icon={FileText}
            title="Request Submitted"
            subtitle="Pending"
          />
          <WorkflowStep
            icon={User}
            title="Department Approval"
            subtitle={approverName || 'Assigned Approver'}
          />
          <WorkflowStep
            icon={DollarSign}
            title="Finance Settlement"
            subtitle="Payment Processed"
            isLast
          />
        </div>
      </CardContent>
    </Card>
  );
};
